import type { IService } from '@experiences/interfaces';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { TenantStatusConstants } from '../../component/tenants/TenantConstants';
import {
    getTenantById,
    getTenants,
    organizationManagementTenantUri,
    tenantByIdUri,
} from '../../services/organization/TenantService';
import {
    accountGlobalId,
    accountLogicalName,
} from '../../store/selectors';

export const MAX_POLL_ATTEMPTS = 5;
export const POLLING_INTERVAL = 2000; // 2 seconds

const isOrchestratorEnabled = (service: IService) =>
    service.serviceType === 'orchestrator' &&
    service.status?.toUpperCase() === TenantStatusConstants.ENABLED;

export const useCheckOrchestratorEnabled = (tenantId: string) => {
    const { data: tenant } = useSWR(
        tenantId ? {
            url: tenantByIdUri,
            id: tenantId,
        } : null,
        getTenantById,
    );

    return tenant?.tenantServiceInstances.some(isOrchestratorEnabled);
};

export const usePollCheckOrchestratorEnabled = () => {
    const organizationGuid = useSelector(accountGlobalId);
    const accountName = useSelector(accountLogicalName);

    const waitForOrchestratorEnabled = useCallback(async () => {
        for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
            const tenants = await getTenants({
                url: organizationManagementTenantUri,
                organizationGuid,
                accountName,
                includeTenantServices: true,
            });

            const enabled = tenants?.some(tenant => tenant.tenantServiceInstances.some(isOrchestratorEnabled));

            if (enabled) {
                return;
            }

            await new Promise(res => setTimeout(res, POLLING_INTERVAL));
        }
    }, [ organizationGuid, accountName ]);

    return { waitForOrchestratorEnabled };
};
